import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import PropTypes from "prop-types";

/**
 * Responsive Pagination Component
 * - Mobile: Shows chevron icons, limited page numbers with ellipsis
 * - Desktop: Shows full text buttons with proper ellipsis
 */
const Pagination = ({ 
  currentPage, 
  totalPages, 
  onPageChange,
  previousLabel = "Previous",
  nextLabel = "Next"
}) => {
  if (totalPages <= 1) return null;

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      onPageChange(page);
    }
  };

  // Generate page numbers with ellipsis
  const getPageNumbers = () => {
    const pages = [];
    const showEllipsis = totalPages > 5;

    if (!showEllipsis) {
      // Show all pages if 5 or fewer
      for (let i = 1; i <= totalPages; i++) {
        pages.push({ type: "page", value: i });
      }
    } else {
      // Always show first page
      pages.push({ type: "page", value: 1 });

      if (currentPage > 3) {
        pages.push({ type: "ellipsis", key: "start" });
      }

      // Calculate range around current page
      let start = Math.max(2, currentPage - 1);
      let end = Math.min(totalPages - 1, currentPage + 1);

      // Adjust range to always show 3 middle pages when possible
      if (currentPage <= 3) {
        end = Math.min(4, totalPages - 1);
      }
      if (currentPage >= totalPages - 2) {
        start = Math.max(2, totalPages - 3);
      }

      for (let i = start; i <= end; i++) {
        pages.push({ type: "page", value: i });
      }

      if (currentPage < totalPages - 2) {
        pages.push({ type: "ellipsis", key: "end" });
      }

      // Always show last page
      if (totalPages > 1) {
        pages.push({ type: "page", value: totalPages });
      }
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <div className="mt-8 p-2 flex justify-center items-center gap-1 sm:gap-2">
      {/* Previous Button */}
      <button
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className={`flex items-center justify-center px-2 sm:px-4 py-2 rounded-md font-nunito-sans text-sm transition-colors duration-200 ${
          currentPage === 1
            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
            : "bg-primary text-white hover:bg-primary/90"
        }`}
        aria-label="Previous page"
      >
        <FontAwesomeIcon icon={faChevronLeft} className="w-3 h-3 sm:mr-2" />
        <span className="hidden sm:inline">{previousLabel}</span>
      </button>

      {/* Page Numbers */}
      <div className="flex items-center gap-1">
        {pageNumbers.map((item, index) => {
          if (item.type === "ellipsis") {
            return (
              <span
                key={item.key}
                className="px-2 py-2 text-gray-500 font-nunito-sans text-sm"
              >
                ...
              </span>
            );
          }

          return (
            <button
              key={item.value}
              onClick={() => handlePageChange(item.value)}
              className={`min-w-[36px] sm:min-w-[40px] px-2 sm:px-3 py-2 rounded-md font-nunito-sans text-sm transition-colors duration-200 ${
                currentPage === item.value
                  ? "bg-primary text-white"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-700"
              }`}
              aria-label={`Page ${item.value}`}
              aria-current={currentPage === item.value ? "page" : undefined}
            >
              {item.value}
            </button>
          );
        })}
      </div>

      {/* Next Button */}
      <button
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className={`flex items-center justify-center px-2 sm:px-4 py-2 rounded-md font-nunito-sans text-sm transition-colors duration-200 ${
          currentPage === totalPages
            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
            : "bg-primary text-white hover:bg-primary/90"
        }`}
        aria-label="Next page"
      >
        <span className="hidden sm:inline">{nextLabel}</span>
        <FontAwesomeIcon icon={faChevronRight} className="w-3 h-3 sm:ml-2" />
      </button>
    </div>
  );
};

Pagination.propTypes = {
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  previousLabel: PropTypes.string,
  nextLabel: PropTypes.string,
};

export default Pagination;
